export interface Punto {
  lat: number;
  lng: number;
}

// Estilo Uber: el marcador recorre el tramo a velocidad constante durante casi
// todo el intervalo entre reportes, así casi no se detiene. Antes era 70% con
// tope de 2.5 s: con reportes cada 15 s se deslizaba 2.5 s y se quedaba quieto
// 12.5 s ("avanza y se para"). 90% y no 100%: llega un poco antes del
// siguiente reporte, así lo dibujado no se va rezagando ("suave pero no
// lento"); si el reporte llega antes, el tramo nuevo sale de lo dibujado.
export const FRACCION_DEL_INTERVALO = 0.9;

export const DURACION_MIN_MS = 250;
// Tope: más allá, el retraso visible deja de valer la suavidad. Era 12 s y
// con reportes cada 60 s (Wialon, medido el 21-oct-2026 en «268 JOSE JUAN»)
// el marcador hacía en 12 s lo que la unidad en 60: iba a 5x su velocidad y
// luego se quedaba 48 s quieto («vuelan», video de Daniel). Con 30 s y
// tramos de hasta TRAMO_MAX_M lo peor es ~2x, y un equipo cada 10-15 s
// (Teltonika/Ruptela) no cambia.
export const DURACION_MAX_MS = 30000;

export function duracionPara(transcurridoMs: number): number {
  if (!Number.isFinite(transcurridoMs) || transcurridoMs <= 0) return DURACION_MIN_MS;
  const ideal = transcurridoMs * FRACCION_DEL_INTERVALO;
  return Math.max(DURACION_MIN_MS, Math.min(DURACION_MAX_MS, ideal));
}

export const VELOCIDAD_MAX_KMH = 150;

export const HUECO_MAX_MS = 3 * 60 * 1000;

// Más allá, la recta A→B deja de parecerse al camino: la 268 en la Federal 2
// (Sonoyta–Caborca) reporta cada 60 s a ~100 km/h, o sea 1.7 km entre puntos,
// y en una curva en S la recta pasó a cientos de metros de la carretera, por
// el desierto. Una curva de carretera de R≈500 m sobre una cuerda de 500 m se
// separa ~60 m, unos 30 px con zoom 16: se ve, pero no "se sale". Un equipo
// cada 15 s a 100 km/h hace ~420 m, así que sigue deslizándose; los tramos
// largos saltan, como en las curvas (Daniel: "prefiero saltos en las curvas
// que atravesarlas"). Pegarlo al camino de verdad es la Roads API (de pago).
export const TRAMO_MAX_M = 500;

const R_TIERRA_M = 6371000;
const rad = (g: number) => (g * Math.PI) / 180;

export function metrosEntre(a: Punto, b: Punto): number {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_TIERRA_M * Math.asin(Math.sqrt(x));
}

export function debeAnimar(
  desde: Punto | null | undefined,
  hasta: Punto,
  transcurridoMs: number,
  opciones: { velocidadMaxKmh?: number; huecoMaxMs?: number; tramoMaxM?: number } = {},
): boolean {
  const vMax = opciones.velocidadMaxKmh ?? VELOCIDAD_MAX_KMH;
  const huecoMax = opciones.huecoMaxMs ?? HUECO_MAX_MS;
  const tramoMax = opciones.tramoMaxM ?? TRAMO_MAX_M;

  if (!desde) return false;
  if (!Number.isFinite(desde.lat) || !Number.isFinite(desde.lng)) return false;
  if (!Number.isFinite(hasta.lat) || !Number.isFinite(hasta.lng)) return false;
  if (!Number.isFinite(transcurridoMs) || transcurridoMs <= 0) return false;

  if (transcurridoMs > huecoMax) return false;

  const m = metrosEntre(desde, hasta);
  if (m <= 0 || m > tramoMax) return false;

  const kmh = (m / 1000) / (transcurridoMs / 3600000);
  return kmh <= vMax;
}

export function interpolar(desde: Punto, hasta: Punto, t: number): Punto {
  const k = Math.max(0, Math.min(1, t));
  return {
    lat: desde.lat + (hasta.lat - desde.lat) * k,
    lng: desde.lng + (hasta.lng - desde.lng) * k,
  };
}

function prefiereSinMovimiento(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  } catch {
    return false;
  }
}

export interface Animacion {
  
  cancelar: () => void;
}

export function animarHacia(
  desde: Punto,
  hasta: Punto,
  aplicar: (p: Punto) => void,
  opciones: { duracionMs?: number; trayecto?: (t: number) => Punto } = {},
): Animacion {
  const duracion = opciones.duracionMs ?? DURACION_MIN_MS;
  const en = opciones.trayecto ?? ((t: number) => interpolar(desde, hasta, t));

  if (typeof requestAnimationFrame !== "function" || prefiereSinMovimiento() || duracion <= 0) {
    aplicar(hasta);
    return { cancelar: () => {} };
  }

  let id = 0;
  let cancelada = false;

  
  const ahoraMs = () => (typeof performance?.now === "function" ? performance.now() : Date.now());
  const inicio = ahoraMs();

  const paso = () => {
    if (cancelada) return;

    const t = (ahoraMs() - inicio) / duracion;
    if (t >= 1) {
      // Con trayecto, el último cuadro también pasa por él (cierra lo que
      // haga por cuadro, p. ej. el giro del puntero).
      if (opciones.trayecto) en(1);
      aplicar(hasta);
      return;
    }

    // Lineal a propósito: tramos encadenados con ease-in-out frenan y arrancan
    // en cada reporte. La interrupción la absorbe quien llama, empezando el
    // tramo nuevo desde la posición dibujada.
    aplicar(en(t));
    id = requestAnimationFrame(paso);
  };

  id = requestAnimationFrame(paso);

  return {
    cancelar: () => {
      cancelada = true;
      if (id) cancelAnimationFrame(id);
    },
  };
}

// Curvas: entre dos reportes sólo se conoce A y B; la recta entre ambos corta
// curvas y glorietas por encima de lo que haya. Se probó pedir la ruta de
// manejo (Directions) y en carreteras divididas daba retornos y rebotes, así
// que la regla es honesta y gratis (Daniel: "prefiero saltos en las curvas
// que atravesarlas"): si el rumbo cambió más de CURVA_GRADOS entre reportes, o
// la recta A→B no va hacia donde apuntaba la unidad, hubo curva → salto.
export const CURVA_GRADOS = 35;

const difAngulo = (a: number, b: number) => Math.abs(((b - a + 540) % 360) - 180);

export function rumboEntre(a: Punto, b: Punto): number {
  const dLng = rad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(rad(b.lat));
  const x = Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) - Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(dLng);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

/**
 * ¿El tramo A→B es recto (se puede deslizar)? Sin rumbos válidos (0 o null en
 * equipos que no lo mandan) se juzga sólo por lo que hay.
 */
export function esTramoRecto(
  desde: Punto,
  hasta: Punto,
  rumboAntes: number | null | undefined,
  rumboAhora: number | null | undefined,
  tolerancia = CURVA_GRADOS,
): boolean {
  const ok = (r: number | null | undefined): r is number => r != null && Number.isFinite(r) && r !== 0;
  if (ok(rumboAntes) && ok(rumboAhora) && difAngulo(rumboAntes, rumboAhora) > tolerancia) return false;
  if (metrosEntre(desde, hasta) < 15) return true; // en un paso corto el rumbo de la recta es ruido
  const recta = rumboEntre(desde, hasta);
  if (ok(rumboAhora) && difAngulo(recta, rumboAhora) > tolerancia) return false;
  if (ok(rumboAntes) && difAngulo(recta, rumboAntes) > tolerancia) return false;
  return true;
}

// Curva suave en vez de salto cuando la vuelta es "normal": entre A y B, una
// cúbica de Bézier con las tangentes en el rumbo de cada reporte (Hermite):
// sale de A hacia donde apuntaba y llega a B apuntando como reporta, así
// dobla la esquina por la calle en vez de cortarla en recta. Sin datos de la
// carretera ($0): en curvas y esquinas comunes queda sobre el camino; en una
// vuelta en U, un brinco de GPS o rumbos que no cuadran con el avance, null
// (quien llama salta, como antes).
export const CURVA_MAX_GRADOS = 100;

export function curvaEntre(
  desde: Punto,
  hasta: Punto,
  rumboAntes: number | null | undefined,
  rumboAhora: number | null | undefined,
): ((t: number) => Punto) | null {
  const ok = (r: number | null | undefined): r is number => r != null && Number.isFinite(r) && r !== 0;
  if (!ok(rumboAntes) || !ok(rumboAhora)) return null;
  if (difAngulo(rumboAntes, rumboAhora) > CURVA_MAX_GRADOS) return null;
  const d = metrosEntre(desde, hasta);
  if (d < 15) return null;
  // El avance tiene que ir "hacia adelante" respecto a los dos rumbos: si la
  // recta A→B apunta para atrás de alguno, los rumbos no son de este tramo.
  const recta = rumboEntre(desde, hasta);
  if (difAngulo(recta, rumboAntes) > 90 || difAngulo(recta, rumboAhora) > 90) return null;

  // Puntos de control a d/3 sobre cada tangente (Hermite → Bézier), en
  // grados: 1° de lat ≈ 111,320 m y la lng se encoge con cos(lat).
  const mLat = 111320;
  const mLng = 111320 * Math.cos(rad(desde.lat));
  const k = d / 3;
  const p1 = { lat: desde.lat + (k * Math.cos(rad(rumboAntes))) / mLat, lng: desde.lng + (k * Math.sin(rad(rumboAntes))) / mLng };
  const p2 = { lat: hasta.lat - (k * Math.cos(rad(rumboAhora))) / mLat, lng: hasta.lng - (k * Math.sin(rad(rumboAhora))) / mLng };
  // ponytail: t uniforme, no por largo de arco; en vueltas ≤ 100° la
  // velocidad aparente varía poco. Reparametrizar si se nota que "acelera".
  return (t: number) => {
    const u = Math.max(0, Math.min(1, t));
    const a = (1 - u) ** 3, b = 3 * (1 - u) ** 2 * u, c = 3 * (1 - u) * u ** 2, e = u ** 3;
    return {
      lat: a * desde.lat + b * p1.lat + c * p2.lat + e * hasta.lat,
      lng: a * desde.lng + b * p1.lng + c * p2.lng + e * hasta.lng,
    };
  };
}
